import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  classes,
  DropdownButton,
  type GlobalProps,
  useSlug,
} from '@bloomberg/media-ui-core';
import {
  useSelect,
  type UseSelectState,
  type UseSelectStateChange,
  type UseSelectStateChangeOptions,
} from 'downshift';

import { DropdownOption } from '../DropdownOption';
import { DropdownOption as DropdownOptionType } from '../props';

import styles from './ButtonDropdown.module.css';

export type ButtonDropdownProps = GlobalProps & {
  id?: string;
  options: DropdownOptionType[];
  defaultOption?: DropdownOptionType;
  disabled?: boolean;
  onSelect: (v?: DropdownOptionType | null) => void;
  className?: string;
  menuPortal?: Element | null;
  icon?: ReactNode;
  selectedItemOverride?: DropdownOptionType;
};

export const ButtonDropdown = React.forwardRef<
  HTMLDivElement,
  ButtonDropdownProps
>(function ButtonDropdownComponent(props: ButtonDropdownProps, ref) {
  const {
    name = 'ButtonDropdown',
    unstyled,
    onSelect,
    options = [],
    defaultOption,
    className,
    menuPortal,
    icon,
    selectedItemOverride,
  } = props;

  const [selectedItem, setSelectedItem] = useState<
    DropdownOptionType | undefined | null
  >(defaultOption || options[0]);

  useEffect(() => {
    setSelectedItem((prevSelectedItem) => {
      const shouldUpdate =
        selectedItemOverride?.value &&
        selectedItemOverride.value !== prevSelectedItem?.value;

      return shouldUpdate ? selectedItemOverride : prevSelectedItem;
    });
  }, [selectedItemOverride]);

  const componentName = useSlug(name);

  const handleOnSelect = useCallback(
    (changes: UseSelectStateChange<DropdownOptionType>) => {
      onSelect(changes.selectedItem);
      setSelectedItem(changes.selectedItem);
    },
    [onSelect]
  );

  const cx = classes([styles.wrapper, className], {
    ...props,
    unstyled,
    name,
  });

  const itemToString = useCallback((option) => option.value, []);

  const stateReducer = useCallback(
    (
      state: UseSelectState<DropdownOptionType>,
      actionAndChanges: UseSelectStateChangeOptions<DropdownOptionType>
    ) => {
      const { changes } = actionAndChanges;

      // Resets tab index when toggling from closed to open state
      if (!state.isOpen && changes.isOpen) {
        return {
          ...changes,
          highlightedIndex: -1,
        };
      }

      return changes;
    },
    []
  );

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useSelect({
    items: options,
    itemToString,
    onSelectedItemChange: handleOnSelect,
    stateReducer,
    selectedItem,
  });

  const renderDropdownMenu = () => {
    return (
      <ul
        data-component={`${componentName}-menu`}
        className={classes([styles.menu, { [styles['menu-open']]: isOpen }])}
        {...getMenuProps()}
      >
        {isOpen &&
          options.map((option, index) => {
            return (
              <DropdownOption
                key={option.value}
                {...option}
                {...getItemProps({ item: option, index })}
                isHighlighted={highlightedIndex === index}
                isSelected={selectedItem?.value === option.value}
              />
            );
          })}
      </ul>
    );
  };

  // downshift randomly generates fake ids for aria categories, so use the id
  // of the element explicitly
  const { id: toggleButtonId } = getToggleButtonProps();
  const toggleButtonOptions = {
    id: toggleButtonId,
    'aria-controls': toggleButtonId,
    'aria-labelledby': toggleButtonId,
  };

  return (
    <div data-component={componentName} className={cx} ref={ref}>
      <DropdownButton
        text={selectedItem?.displayValue || selectedItem?.value || 'Unknown'}
        isOpen={isOpen}
        unstyled={unstyled}
        {...getToggleButtonProps(toggleButtonOptions)}
        icon={icon}
      />
      {!menuPortal && renderDropdownMenu()}
      {menuPortal && createPortal(renderDropdownMenu(), menuPortal)}
    </div>
  );
});
